import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

export default function BalanceGeneral() {
  const [fechaCorte, setFechaCorte] = useState("2024-11-30");

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    activos: { circulante: [], fijo: [] },
    pasivos: { corto_plazo: [], largo_plazo: [] },
    capital: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.BALANCE_GENERAL, {
          params: { fecha_corte: fechaCorte },
        });
        setData(response.data);
      } catch (error) {
        console.error("Error al cargar balance general:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fechaCorte]);

  const { activos, pasivos, capital } = data;

  const totalActivoCirculante = activos.circulante.reduce((sum, item) => sum + item.monto, 0);
  const totalActivoFijo = activos.fijo.reduce((sum, item) => sum + item.monto, 0);
  const totalActivos = totalActivoCirculante + totalActivoFijo;

  const totalPasivoCorto = pasivos.corto_plazo.reduce((sum, item) => sum + item.monto, 0);
  const totalPasivoLargo = pasivos.largo_plazo.reduce((sum, item) => sum + item.monto, 0);
  const totalPasivos = totalPasivoCorto + totalPasivoLargo;

  const totalCapital = capital.reduce((sum, item) => sum + item.monto, 0);
  const totalPasivoCapital = totalPasivos + totalCapital;

  const LineaItem = ({ item }) => (
    <MDBox display="flex" justifyContent="space-between" py={0.5} px={3}>
      <MDTypography variant="button" fontWeight="regular">
        {item.cuenta}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium">
        {formatCurrency(item.monto)}
      </MDTypography>
    </MDBox>
  );

  LineaItem.propTypes = {
    item: PropTypes.shape({
      cuenta: PropTypes.string.isRequired,
      monto: PropTypes.number.isRequired,
    }).isRequired,
  };

  const SubTotal = ({ label, monto, color = "text" }) => (
    <MDBox display="flex" justifyContent="space-between" py={1} px={3} sx={{ bgcolor: "grey.100" }}>
      <MDTypography variant="button" fontWeight="bold" color={color}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold" color={color}>
        {formatCurrency(monto)}
      </MDTypography>
    </MDBox>
  );

  SubTotal.propTypes = {
    label: PropTypes.string.isRequired,
    monto: PropTypes.number.isRequired,
    color: PropTypes.string,
  };

  const Total = ({ label, monto }) => (
    <MDBox
      display="flex"
      justifyContent="space-between"
      py={1.5}
      px={3}
      sx={{ bgcolor: "info.main" }}
    >
      <MDTypography variant="h6" fontWeight="bold" color="white">
        {label}
      </MDTypography>
      <MDTypography variant="h6" fontWeight="bold" color="white">
        {formatCurrency(monto)}
      </MDTypography>
    </MDBox>
  );

  Total.propTypes = {
    label: PropTypes.string.isRequired,
    monto: PropTypes.number.isRequired,
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Balance General (Estado de Situación Financiera)
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <TextField
                    type="date"
                    label="Fecha de Corte"
                    value={fechaCorte}
                    onChange={(e) => setFechaCorte(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <MDButton variant="gradient" color="info">
                    <Icon>print</Icon>&nbsp;Imprimir
                  </MDButton>
                </MDBox>
              </MDBox>

              <Grid container spacing={3}>
                {/* ACTIVOS */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <MDBox py={2} px={3} sx={{ bgcolor: "info.main" }}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        ACTIVOS
                      </MDTypography>
                    </MDBox>

                    {/* Activo Circulante */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="info">
                          Activo Circulante
                        </MDTypography>
                      </MDBox>
                      {activos.circulante.map((item, index) => (
                        <LineaItem key={index} item={item} />
                      ))}
                      <SubTotal
                        label="Total Activo Circulante"
                        monto={totalActivoCirculante}
                        color="info"
                      />
                    </MDBox>

                    <Divider />

                    {/* Activo Fijo */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="info">
                          Activo Fijo
                        </MDTypography>
                      </MDBox>
                      {activos.fijo.map((item, index) => (
                        <LineaItem key={index} item={item} />
                      ))}
                      <SubTotal label="Total Activo Fijo" monto={totalActivoFijo} color="info" />
                    </MDBox>

                    <Total label="TOTAL ACTIVOS" monto={totalActivos} />
                  </Card>
                </Grid>

                {/* PASIVOS Y CAPITAL */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <MDBox py={2} px={3} sx={{ bgcolor: "warning.main" }}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        PASIVOS Y CAPITAL
                      </MDTypography>
                    </MDBox>

                    {/* Pasivo Corto Plazo */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="warning">
                          Pasivo a Corto Plazo
                        </MDTypography>
                      </MDBox>
                      {pasivos.corto_plazo.map((item, index) => (
                        <LineaItem key={index} item={item} />
                      ))}
                      <SubTotal
                        label="Total Pasivo Corto Plazo"
                        monto={totalPasivoCorto}
                        color="warning"
                      />
                    </MDBox>

                    <Divider />

                    {/* Pasivo Largo Plazo */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="warning">
                          Pasivo a Largo Plazo
                        </MDTypography>
                      </MDBox>
                      {pasivos.largo_plazo.map((item, index) => (
                        <LineaItem key={index} item={item} />
                      ))}
                      <SubTotal
                        label="Total Pasivo Largo Plazo"
                        monto={totalPasivoLargo}
                        color="warning"
                      />
                      <SubTotal label="TOTAL PASIVOS" monto={totalPasivos} color="error" />
                    </MDBox>

                    <Divider />

                    {/* Capital */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="success">
                          Capital Contable
                        </MDTypography>
                      </MDBox>
                      {capital.map((item, index) => (
                        <LineaItem key={index} item={item} />
                      ))}
                      <SubTotal label="TOTAL CAPITAL" monto={totalCapital} color="success" />
                    </MDBox>

                    <Total label="TOTAL PASIVO + CAPITAL" monto={totalPasivoCapital} />
                  </Card>
                </Grid>

                {/* Indicadores */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <MDBox p={3}>
                      <MDTypography variant="h6" fontWeight="medium" mb={2}>
                        Análisis del Balance
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <MDBox
                            textAlign="center"
                            p={2}
                            sx={{ bgcolor: "success.lighter", borderRadius: 2 }}
                          >
                            <MDTypography variant="caption" color="text">
                              Razón Circulante
                            </MDTypography>
                            <MDTypography variant="h5" fontWeight="bold" color="success">
                              {(totalActivoCirculante / totalPasivoCorto).toFixed(2)}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              (Activo/Pasivo Circulante)
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <MDBox
                            textAlign="center"
                            p={2}
                            sx={{ bgcolor: "info.lighter", borderRadius: 2 }}
                          >
                            <MDTypography variant="caption" color="text">
                              Razón de Endeudamiento
                            </MDTypography>
                            <MDTypography variant="h5" fontWeight="bold" color="info">
                              {((totalPasivos / totalActivos) * 100).toFixed(1)}%
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              (Pasivos/Activos)
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <MDBox
                            textAlign="center"
                            p={2}
                            sx={{ bgcolor: "warning.lighter", borderRadius: 2 }}
                          >
                            <MDTypography variant="caption" color="text">
                              Capital de Trabajo
                            </MDTypography>
                            <MDTypography variant="h5" fontWeight="bold" color="warning">
                              {formatCurrency(totalActivoCirculante - totalPasivoCorto)}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              (Act. Circ. - Pas. Circ.)
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <MDBox
                            textAlign="center"
                            p={2}
                            sx={{ bgcolor: "error.lighter", borderRadius: 2 }}
                          >
                            <MDTypography variant="caption" color="text">
                              Verificación Balance
                            </MDTypography>
                            <MDTypography
                              variant="h5"
                              fontWeight="bold"
                              color={totalActivos === totalPasivoCapital ? "success" : "error"}
                            >
                              {totalActivos === totalPasivoCapital ? "✓ OK" : "✗ ERROR"}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              Activos = Pasivos + Capital
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
